import React from "react";
import { Box, Typography, Button, TextField, CircularProgress } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { downloadContentFile } from "../../../api/backend";
import DS from "./detailTokens";

const sectionLabelSx = {
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: DS.ink3,
  mb: "8px",
};

function InstructorNotesField({ sessionId, value, canEditNotes, saving, onSave }) {
  const [draft, setDraft] = React.useState(value);
  const dirty = draft !== value;
  React.useEffect(() => setDraft(value), [value]);

  if (!canEditNotes) {
    return (
      <Typography sx={{ fontSize: 13, color: DS.ink2, whiteSpace: "pre-wrap" }}>
        {value || <em style={{ color: DS.ink4 }}>No notes yet.</em>}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <TextField
        multiline
        minRows={2}
        maxRows={8}
        fullWidth
        size="small"
        placeholder="Notes for this session's instructor(s)…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      {dirty && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            size="small"
            variant="contained"
            disabled={saving}
            onClick={() => onSave(sessionId, draft)}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: "12px", borderRadius: "8px", height: 28, boxShadow: "none" }}
          >
            {saving ? "Saving…" : "Save notes"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * Expanded row content: topics, private instructor notes and session materials (documents, skill check sheets, tests).
 * The parent loads notes and documents when a row is expanded and passes the current state down.
 */
export default function SessionDetails({ session, topics, materials, docsBusy, notesLoading, notes, notesSaving, canEdit, canSeeNotes, onSaveNotes }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: courseId } = useParams();
  const tPrimary = theme.palette.primary.main;
  const tPrimary10 = alpha(tPrimary, 0.06);

  async function openDoc(item) {
    const ct = (item.contentType || "").toUpperCase();
    const isLink = ct === "LINK" || (!!item.url && !item.hasFile);
    if (isLink && item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const blobUrl = await downloadContentFile(item.id);
      window.open(blobUrl, "_blank");
    } catch (e) {
      console.error("Failed to open content", e);
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Box>
        <Typography sx={sectionLabelSx}>Topics covered</Typography>
        {topics.length === 0 ? (
          <Typography sx={{ fontSize: 13, fontStyle: "italic", color: DS.ink4 }}>
            No topics listed yet.
            {canEdit ? " Use Edit to add topics." : ""}
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: "18px", display: "flex", flexDirection: "column", gap: "6px" }}>
            {topics.map((topic) => (
              <Box component="li" key={topic} sx={{ fontSize: 13.5, color: DS.ink2, lineHeight: 1.4 }}>
                {topic}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {canSeeNotes && (
        <Box>
          <Typography sx={sectionLabelSx}>Instructor notes (private)</Typography>
          <Typography sx={{ fontSize: 11.5, color: DS.ink4, mb: "6px" }}>
            Only visible to the course coordinator and instructors — never shown to students.
          </Typography>
          {notesLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: DS.ink3, fontSize: 13 }}>
              <CircularProgress size={16} sx={{ color: tPrimary }} />
              Loading notes…
            </Box>
          ) : (
            <InstructorNotesField
              sessionId={session.id}
              value={notes}
              canEditNotes={canEdit}
              saving={notesSaving}
              onSave={onSaveNotes}
            />
          )}
        </Box>
      )}

      <Box>
        <Typography sx={sectionLabelSx}>Session materials</Typography>
        {docsBusy ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", color: DS.ink3, fontSize: 13 }}>
            <CircularProgress size={16} sx={{ color: tPrimary }} />
            Loading materials…
          </Box>
        ) : materials.isEmpty ? (
          <Typography sx={{ fontSize: 13, fontStyle: "italic", color: DS.ink4 }}>
            No materials assigned to this session yet.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {materials.docs.map((item) => {
              const ct = (item.contentType || "").toUpperCase();
              const kind = ct === "VIDEO" ? "video" : ct === "LINK" || (!!item.url && !item.hasFile) ? "link" : "file";
              const Icon =
                kind === "video"
                  ? PlayCircleOutlineIcon
                  : kind === "link"
                    ? LinkOutlinedIcon
                    : InsertDriveFileOutlinedIcon;
              return (
                <Box
                  key={`doc-${item.id}`}
                  onClick={() => openDoc(item)}
                  sx={{
                    bgcolor: DS.surface,
                    border: `1px solid ${DS.line}`,
                    borderRadius: "12px",
                    p: "12px 14px",
                    display: "grid",
                    gridTemplateColumns: "40px 1fr auto",
                    gap: "12px",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": { borderColor: "var(--color-hairline-cool)", bgcolor: DS.surface2 },
                  }}
                >
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "10px",
                    bgcolor: kind === "video" ? DS.danger50 : kind === "link" ? DS.blue50 : tPrimary10,
                    color: kind === "video" ? DS.danger : kind === "link" ? DS.blue : tPrimary,
                    display: "grid", placeItems: "center",
                  }}>
                    <Icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DS.ink }} noWrap>
                      {item.title || item.fileName || item.url || "Document"}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: DS.ink3 }}>
                      {kind === "video" ? "Video" : kind === "link" ? "Link" : "Document"}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: tPrimary }}>Open</Typography>
                </Box>
              );
            })}
            {materials.checklists.map((cl) => (
              <Box
                key={`cl-${cl.id}`}
                sx={{
                  bgcolor: DS.surface,
                  border: `1px solid ${DS.line}`,
                  borderRadius: "12px",
                  p: "12px 14px",
                  display: "grid",
                  gridTemplateColumns: { xs: "40px 1fr", sm: "40px 1fr auto" },
                  gap: "12px",
                  alignItems: "center",
                }}
              >
                <Box sx={{
                  width: 40, height: 40, borderRadius: "10px",
                  bgcolor: "color-mix(in srgb, var(--color-status-success) 12%, transparent)",
                  color: "var(--color-status-success)",
                  display: "grid", placeItems: "center",
                }}>
                  <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DS.ink }}>
                    {cl.title || "Skill check sheet"}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: DS.ink3 }}>
                    {(cl.items || []).length || cl.itemCount || 0} skills
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigate(`/course-management/${courseId}/checklists/${cl.id}/assess`)}
                  sx={{
                    textTransform: "none", fontWeight: 600, fontSize: "12px",
                    borderRadius: "8px", height: 30, px: "12px", boxShadow: "none",
                    gridColumn: { xs: "1 / -1", sm: "auto" },
                  }}
                >
                  Assess
                </Button>
              </Box>
            ))}
            {materials.tests.map((test) => (
              <Box
                key={`test-${test.id}`}
                sx={{
                  bgcolor: DS.surface,
                  border: `1px solid ${DS.line}`,
                  borderRadius: "12px",
                  p: "12px 14px",
                  display: "grid",
                  gridTemplateColumns: { xs: "40px 1fr", sm: "40px 1fr auto" },
                  gap: "12px",
                  alignItems: "center",
                }}
              >
                <Box sx={{
                  width: 40, height: 40, borderRadius: "10px",
                  bgcolor: DS.purple50, color: DS.purple,
                  display: "grid", placeItems: "center",
                }}>
                  <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DS.ink }}>
                    {test.title || "Test"}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: DS.ink3 }}>
                    Written test
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigate(`/course-management/${courseId}/tests/${test.id}/assess`)}
                  sx={{
                    textTransform: "none", fontWeight: 600, fontSize: "12px",
                    borderRadius: "8px", height: 30, px: "12px", boxShadow: "none",
                    gridColumn: { xs: "1 / -1", sm: "auto" },
                  }}
                >
                  Review
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
